import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// Any case's money (billing PR #6, 2026-10-30) - SHADOW and DARK.
//   /billing/case/:caseId   one case of any kind (workers' comp, PI lien, anything Raintree bills):
//                           every charge with Raintree's amounts, the payment lines staff keyed,
//                           the checks, and the EOB lines Money In matched to it - one dollar from
//                           the visit to the posted payment.
// Behind the server's billing gate like the rest of Billing (anyone else is sent home as if the page
// didn't exist). The URL carries the portal's case id only. Reads only: nothing here changes Raintree.

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function Note({ children, tone = "slate" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600";
  return <div className={`mt-4 rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

const STATE_STYLE = {
  approved: "bg-emerald-50 text-emerald-800",
  proposed: "bg-blue-50 text-blue-800",
  open: "bg-slate-100 text-slate-700",
  rejected: "bg-rose-50 text-rose-800",
};

function CaseView({ caseId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showVoid, setShowVoid] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api
      .get(`/billing/cases/${caseId}`)
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 404 ? "There's no such case." : "Couldn't load the case."));
    return () => {
      live = false;
    };
  }, [caseId]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const c = data.case;
  const t = data.totals;
  const ck = data.checks;
  const isPI = c.lien_class === "PI_LIEN";
  const isWC = c.lien_class === "WC_LIEN";
  const anyTransfers = data.charges.some((x) => Number(x.rt_transfers_net || 0) !== 0);
  const active = data.coverages.filter((v) => v.is_active);
  return (
    <div data-testid="billing-case">
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {[c.last_name, [c.first_name, c.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
          </h2>
          <p className="text-sm text-slate-500">
            Raintree #{c.raintree_patient_number || "—"} · Case #{c.case_number || "—"} · {data.kind} · {c.status}
            {" "}· Care {usDate(c.start_of_care)} to {usDate(c.care_end_date)}
            {isWC || c.date_of_injury ? ` · Injury ${usDate(c.date_of_injury)}` : ""}
          </p>
          <p className="text-sm text-slate-500" data-testid="billing-case-coverages">
            {active.length === 0 ? "No coverage on file" : active.map((v) => (
              <span key={v.coverage_id} className="mr-3">
                {v.is_attorney ? "Attorney" : "Payer"}: {v.name || "—"}
                {v.claim_number ? ` · Claim #${v.claim_number}` : ""}
              </span>
            ))}
          </p>
        </div>
        {isPI && c.is_lien_case && (
          <Link to={`/billing/pi/case/${c.case_id}`} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="billing-case-open-pi">
            PI lien view and statement
          </Link>
        )}
      </div>
      {(ck.charges_off_their_postings > 0 || ck.charges_off_the_identity > 0 || ck.unapplied_postings > 0) && (
        <Note tone="amber">
          Checks: {ck.charges_off_their_postings} charge(s) where Raintree's amounts differ from the payment lines on file;{" "}
          {ck.charges_off_the_identity} where charge − payments − adjustments (net of reversals) − transfers ≠ balance;{" "}
          {ck.unapplied_postings} payment line(s) on the case not applied to a charge.
        </Note>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Charges" value={money(t.billed)} sub={`${data.charges.length} services`} />
        <Stat label="Payments" value={money(t.payments)} />
        <Stat label="Adjustments" value={money(t.adjustments)}
              sub={Number(t.adjustment_reversals || 0) !== 0 ? `net of ${money(t.adjustment_reversals)} reversed` : undefined} />
        <Stat label="Transfers" value={money(t.transfers)} />
        <Stat label="Balance" value={money(t.balance)} testid="billing-case-balance" />
      </div>

      <h3 className="mt-6 text-base font-semibold text-slate-800">Charges (from Raintree, last night)</h3>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="billing-case-charges">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">CPT</th>
              <th className="px-3 py-2">Mod</th>
              <th className="px-3 py-2 text-right">Units</th>
              <th className="px-3 py-2 text-right">Charge</th>
              <th className="px-3 py-2 text-right">Payments</th>
              <th className="px-3 py-2 text-right">Adjustments</th>
              {anyTransfers && <th className="px-3 py-2 text-right">Transfers</th>}
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2">Last payment</th>
              <th className="px-3 py-2">EOB</th>
              <th className="px-3 py-2">Checks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.charges.map((x) => (
              <tr key={x.charge_id}>
                <td className="px-3 py-1.5">{usDate(x.date_of_service)}</td>
                <td className="px-3 py-1.5">{x.cpt_code || "—"}</td>
                <td className="px-3 py-1.5 text-slate-600">{x.modifiers || ""}</td>
                <td className="px-3 py-1.5 text-right">{Number(x.units || 0)}</td>
                <td className="px-3 py-1.5 text-right">{money(x.billed_amount)}</td>
                <td className="px-3 py-1.5 text-right">{money(x.rt_payments_amount)}</td>
                <td className="px-3 py-1.5 text-right">{money(x.adjustments_net)}</td>
                {anyTransfers && <td className="px-3 py-1.5 text-right">{money(x.rt_transfers_net)}</td>}
                <td className="px-3 py-1.5 text-right font-medium">{money(x.rt_current_balance)}</td>
                <td className="px-3 py-1.5 text-slate-600">{usDate(x.rt_last_payment_date)}</td>
                <td className="px-3 py-1.5 text-xs text-slate-600">
                  {x.eob_line_ids.length ? `${x.eob_line_ids.length} line${x.eob_line_ids.length === 1 ? "" : "s"}` : "—"}
                </td>
                <td className="px-3 py-1.5 text-xs">
                  {x.recon_ok && x.identity_ok ? <span className="text-emerald-700">ok</span> : (
                    <span className="text-amber-800">
                      {!x.recon_ok && "≠ payment lines "}{!x.identity_ok && "≠ balance math"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.void_charges.length > 0 && (
        <button className="mt-2 text-sm text-slate-600 underline" onClick={() => setShowVoid(!showVoid)}>
          {showVoid ? "Hide" : "Show"} {data.void_charges.length} void charge(s)
        </button>
      )}
      {showVoid && (
        <ul className="mt-2 text-sm text-slate-500">
          {data.void_charges.map((x) => (
            <li key={x.charge_id}>{usDate(x.date_of_service)} · {x.cpt_code} · {money(x.billed_amount)} (void)</li>
          ))}
        </ul>
      )}

      <h3 className="mt-6 text-base font-semibold text-slate-800" data-testid="billing-case-eob-title">
        EOB lines matched to this case ({data.eob_lines.length})
      </h3>
      {data.eob_lines.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">No EOB line from the bot is matched to this case yet.</p>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm" data-testid="billing-case-eob-lines">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">EOB</th>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">CPT</th>
                <th className="px-3 py-2 text-right">Billed</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Adjustment</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2 text-right">Shadow payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.eob_lines.map((l) => (
                <tr key={l.remit_line_id}>
                  <td className="px-3 py-1.5">
                    <Link to={`/billing/eob/${l.remit_id}`} className="text-blue-600 hover:underline">#{l.remit_id}</Link>
                    {l.line_number ? <span className="text-xs text-slate-400"> line {l.line_number}</span> : null}
                  </td>
                  <td className="px-3 py-1.5">{usDate(l.received_at)}</td>
                  <td className="px-3 py-1.5">{usDate(l.date_of_service)}</td>
                  <td className="px-3 py-1.5">{l.cpt_code || "—"}</td>
                  <td className="px-3 py-1.5 text-right">{money(l.billed_amount)}</td>
                  <td className="px-3 py-1.5 text-right">{money(l.paid_amount)}</td>
                  <td className="px-3 py-1.5 text-right">{money(l.adjustment_amount)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded px-2 py-0.5 text-xs ${STATE_STYLE[l.state] || "bg-slate-100 text-slate-700"}`}>{l.state}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right">{Number(l.shadow_payment) ? money(l.shadow_payment) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mt-6 text-base font-semibold text-slate-800">Payment lines from Raintree ({data.postings.length})</h3>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="billing-case-postings">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Posted</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Charge</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.postings.map((p) => (
              <tr key={p.posting_id}>
                <td className="px-3 py-1.5">{usDate(p.posted_on)}</td>
                <td className="px-3 py-1.5">{p.posting_type}</td>
                <td className="px-3 py-1.5 text-right">{money(p.amount)}</td>
                <td className="px-3 py-1.5 text-slate-600">{p.charge_id ? `#${p.charge_id}` : "not applied"}</td>
                <td className="px-3 py-1.5 text-slate-600">{p.raintree_reason_code || p.adjustment_reason || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Shadow mode: the EOB lines and their shadow payments live in the portal only; Raintree's numbers come from the
        nightly mirror. Nothing on this page changes Raintree.
      </p>
    </div>
  );
}

export default function BillingCase() {
  const { user } = useAuth();
  const { caseId } = useParams();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;
  return (
    <div className="p-8 max-w-7xl" data-testid="billing-case-page">
      <BillingNav />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>{" / "}Case
      </div>
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">Case</h1>
      <p className="mt-1 text-sm text-slate-500">
        Shadow mode. Read from the Raintree mirror and Money In; nothing here changes Raintree.
      </p>
      <CaseView caseId={caseId} />
    </div>
  );
}
